/**
 * Live calamity banners.
 *
 * Polls the calamity REST endpoint and keeps the on-page banners in sync for visitors who are
 * already on the page: a newly published calamity fades + slides in, and one that is no longer
 * active fades + slides out. Diffing is by the data-calamity-id attribute.
 *
 * Configuration is provided server-side via wp_variables.calamity
 * ({ endpoint, contextId, interval }), localised in NLDS_Theme::enqueue_client_scripts().
 *
 * @package nl-design-system-theme
 */
export default class CalamityBanner {
  constructor() {
    this.config = ( window.wp_variables && window.wp_variables.calamity ) || {};
    this.region = document.querySelector( "[data-calamity-region]" );

    // Kill switch: never start polling when the banner is disabled, even if a cached page still holds
    // the region markup (it was rendered before the banner was turned off).
    if (!this.region || !this.config.endpoint || this.config.disabled) {
      return;
    }

    this.enteringClass = "denhaag-calamity-banner--entering";
    this.leavingClass = "denhaag-calamity-banner--leaving";
    this.poll = this.poll.bind( this );

    // Live-region announcement buffer: messages collected during one poll are flushed together on the
    // next frame (see announce()), so concurrent calamities are announced as one discrete change.
    this.pendingAnnouncement = "";
    this.announceScheduled = false;

    // Poll on the server-configured interval (wp_variables.calamity.interval), clamped to a 5s floor.
    this.timer = window.setInterval( this.poll, Math.max( 5, this.config.interval || 5 ) * 1000 );
    document.addEventListener( "visibilitychange", () => !document.hidden && this.poll() );
  }

  /**
   * Stop polling for good. Called when the server reports the banner has been disabled, so a page that
   * was rendered/cached while it was still enabled stops fetching instead of polling forever.
   */
  stop() {
    if (this.timer) {
      window.clearInterval( this.timer );
      this.timer = null;
    }
    this.stopped = true;
  }

  /**
   * Fetch the current active calamities for this page and reconcile them with the DOM.
   */
  async poll() {
    if (this.stopped) {
      return;
    }

    let banners;
    try {
      const url = new URL( this.config.endpoint, window.location.origin );
      // Only the page IDs a calamity actually targets are sent (most pages send none), so the cached
      // endpoint stays to one shared entry plus one per targeted page. Omit the param entirely when empty.
      const contextId = [].concat( this.config.contextId || [] ).filter( Boolean ).join( "," );
      if (contextId) {
        url.searchParams.set( "context_id", contextId );
      }

      const response = await fetch( url, { headers: { Accept: "application/json" } } );
      // Anything other than a 200 means the endpoint isn't serving banners as expected; stop polling
      // for good so a failing/redirecting backend isn't hammered every interval.
      if (response.status !== 200) {
        this.stop();
        return;
      }
      banners = await response.json();
    } catch (error) {
      return; // Network/parse hiccup; try again next interval.
    }

    // Server kill switch: stop polling for good and clear any banners still on the page.
    if (banners && !Array.isArray( banners ) && banners.disabled) {
      this.stop();
      this.sync( [] );
      return;
    }

    if (Array.isArray( banners )) {
      this.sync( banners );
    }
  }

  /**
   * Add newly-active banners, remove ones no longer active, and refresh ones whose content changed.
   *
   * @param {Array<{id:number, modified:number, html:string}>} banners The current active calamities.
   */
  sync(banners) {
    const incoming = new Map( banners.map( (banner) => [String( banner.id ), banner] ) );
    const current = new Map(
      [...this.region.querySelectorAll( "[data-calamity-id]" )].map( (el) => [el.dataset.calamityId, el] )
    );

    // Remove banners that are no longer active.
    current.forEach( (el, id) => !incoming.has( id ) && this.leave( el ) );

    incoming.forEach( (banner, id) => {
      const existing = current.get( id );

      if (!existing) {
        this.enter( banner.html ); // New calamity.
      } else if (existing.dataset.calamityModified !== String( banner.modified )) {
        this.update( existing, banner.html ); // Edited calamity → re-render in place.
      }
    } );
  }

  /**
   * Parse a rendered banner string into an element.
   *
   * @param {string} html The rendered banner markup.
   *
   * @return {HTMLElement|null}
   */
  parse(html) {
    const template = document.createElement( "div" );
    template.innerHTML = html.trim();

    return template.firstElementChild;
  }

  /**
   * Insert a new banner and play the enter animation.
   *
   * @param {string} html The rendered banner markup.
   */
  enter(html) {
    const banner = this.parse( html );

    if (!banner) {
      return;
    }

    banner.classList.add( this.enteringClass );
    this.region.appendChild( banner );
    this.announce( banner );

    // Next frame: drop the entering class so it transitions into place.
    requestAnimationFrame( () => requestAnimationFrame( () => banner.classList.remove( this.enteringClass ) ) );
  }

  /**
   * Announce a newly added calamity to screen readers via the assertive live region.
   *
   * Announces the whole message — the visually-hidden "Calamity:" prefix + title, then the body text —
   * not just the heading, so screen-reader users get the full alert. Messages are buffered and written
   * on the next animation frame: that batches calamities arriving in the same poll into one discrete
   * change, and decouples the live-region update from the DOM insertion so assistive tech reliably
   * registers it as an announcement (rather than coalescing the two mutations).
   *
   * @param {HTMLElement} banner The banner element that was just inserted.
   */
  announce(banner) {
    const announcer = this.region.querySelector( "[data-calamity-announcer]" );
    if (!announcer) {
      return;
    }

    const title = banner.querySelector( ".denhaag-calamity-banner__title" );
    const body = banner.querySelector( ".denhaag-calamity-banner__body" );
    const message = this.joinSentences( [title && title.textContent, body && body.textContent] );
    if (!message) {
      return;
    }

    this.pendingAnnouncement = this.joinSentences( [this.pendingAnnouncement, message] );

    if (!this.announceScheduled) {
      this.announceScheduled = true;
      requestAnimationFrame( () => {
        announcer.textContent = this.pendingAnnouncement;
        this.pendingAnnouncement = "";
        this.announceScheduled = false;
      } );
    }
  }

  /**
   * Move focus to a stable element before a banner that currently holds focus is destroyed,
   * so keyboard/screen-reader users are not dropped to <body>. Prefers another remaining banner,
   * otherwise the calamity region itself.
   *
   * @param {HTMLElement} banner The banner about to be removed/replaced.
   */
  restoreFocus(banner) {
    const fallback = [...this.region.querySelectorAll( "[data-calamity-id]" )].find( (el) => el !== banner ) || this.region;

    fallback.setAttribute( "tabindex", "-1" );
    fallback.focus();
  }

  /**
   * Join text fragments into one spoken string, separating with ". " but not doubling punctuation
   * when a fragment already ends in a sentence terminator (so "Body one." + next reads cleanly).
   *
   * @param {Array<string|null|undefined>} parts The fragments, in order.
   *
   * @return {string}
   */
  joinSentences(parts) {
    return parts
      .map( (part) => ( part || "" ).trim() )
      .filter( Boolean )
      .reduce( (acc, part) => ( acc ? `${acc}${/[.!?]$/.test( acc ) ? " " : ". "}${part}` : part ), "" );
  }

  /**
   * Replace an existing banner's markup in place (content was edited).
   *
   * @param {HTMLElement} existing The current banner element.
   * @param {string} html The freshly rendered banner markup.
   */
  update(existing, html) {
    const banner = this.parse( html );

    if (banner) {
      // Preserve focus across the in-place replace (replaceWith destroys the focused node).
      const hadFocus = existing.contains( document.activeElement );
      existing.replaceWith( banner );
      if (hadFocus) {
        this.restoreFocus( existing );
      }
    }
  }

  /**
   * Play the leave animation, then remove the banner.
   *
   * @param {HTMLElement} banner The banner element to remove.
   */
  leave(banner) {
    if (banner.dataset.leaving) {
      return;
    }

    // If focus is inside the banner we're about to remove, move it out first so it isn't lost to <body>.
    if (banner.contains( document.activeElement )) {
      this.restoreFocus( banner );
    }

    banner.dataset.leaving = "1";
    banner.classList.add( this.leavingClass );

    const remove = () => banner.remove();
    banner.addEventListener( "transitionend", remove, { once: true } );
    // Fallback when there is no transition (e.g. prefers-reduced-motion).
    window.setTimeout( remove, 600 );
  }
}
